import { Typography, Modal, IconNode, Button } from '@components/base';
import { TextField, InputDatePicker } from '@components/common';

import { useFormContext, Controller } from 'react-hook-form';

import Images from '@assets/images';
import { addBLDetails } from '@services/task.service';
import notify from '@helpers/toastify-helper';
import moment from 'moment';

import css from './index.module.scss';
import { useState } from 'react';
import { getDiffBetweenTwoDates } from '@helpers/utils';

interface Props {
  open: boolean;
  onClose: () => void;
  taskData: any;
  onComplete?: () => void;
  taskName: 'AWB';
  transportation_mode: 'AIR';
  sendStatusToCard: (data: boolean) => void;
}

const AWBDetailsModal = ({
  open,
  onClose,
  taskData,
  onComplete,
  taskName,
  sendStatusToCard
}: any) => {
  const { task_id, export_clearance_obtained_actual_date } = taskData;

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting }
  } = useFormContext();

  const onFormSubmit = async (data: any) => {
    const dateDiff = getDiffBetweenTwoDates(data.awb_date, export_clearance_obtained_actual_date);

    if (dateDiff < 0) {
      setError('awb_date', {
        message:
          'AWB Date cannot be before Export Clearance Obtained Date, contact Tech Team to back‑date'
      });
      return;
    }

    const body = {
      awb_service_name: data.awb_service_name,
      awb_date: moment(data.awb_date).format('YYYY-MM-DD'),
      awb_number: data.awb_number
    };

    const response = await addBLDetails(task_id, body);
    if (response.success) {
      notify({ message: 'AWB details added successfully' });
      onComplete?.();
      onClose();
      sendStatusToCard?.(true);
    } else {
      notify({
        message: response.error ?? 'Something went wrong!',
        severity: 'error'
      });
    }
  };

  const toggleDatePicker = () => setIsDatePickerOpen((prev) => !prev);

  return (
    <Modal open={open} onClose={onClose}>
      <div className={`${css.modalWrapper} ${isDatePickerOpen ? css.modalWrapperOpen : ''}`}>
        <section className={css.modalHeader}>
          <Typography variant="h4">Add AWB Details</Typography>
          <IconNode
            src={Images.crossBlack}
            alt="close icon"
            component="button"
            className={css.closeButton}
            onClick={onClose}
          />
        </section>

        <form noValidate className={css.formWrapper} onSubmit={handleSubmit(onFormSubmit)}>
          <section className={css.modalContent}>
            <Controller
              name="awb_service_name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  required
                  enterKeyHint="next"
                  label="AWB Service Name"
                  placeholder="Enter AWB service name"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  rootClassName={css.awbServiceName}
                />
              )}
            />

            <Controller
              name="awb_date"
              control={control}
              render={({ field, fieldState }) => (
                <InputDatePicker
                  {...field}
                  required
                  setDatePickerOpen={setIsDatePickerOpen}
                  value={field.value}
                  placeholder="Select date"
                  label="AWB Date"
                  onSelect={(day: Date | undefined) => {
                    field.onChange(day ?? null);
                    toggleDatePicker();
                  }}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  rootClassName={css.fieldSpacing}
                />
              )}
            />

            <Controller
              name="awb_number"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  required
                  enterKeyHint="next"
                  label="AWB Number"
                  placeholder="Enter AWB number"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  rootClassName={isDatePickerOpen ? css.fieldWrapperOpenModal : css.fieldWrapper}
                />
              )}
            />
          </section>

          <section className={css.modalFooter}>
            <Button variant="outlined-secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              Save
            </Button>
          </section>
        </form>
      </div>
    </Modal>
  );
};

export default AWBDetailsModal;
